import React, { useEffect, useState } from "react";
import moment from "moment";
import { Tabs, Button } from "antd";
import { LinkOutlined } from "@ant-design/icons";
import { isEmpty } from "lodash";
import NoData from "assets/svgs/no_data/NoData";
import SoftBadge from "modules/common/SoftBadge";
import { RESULT_CHOLESTEROL, RESULT_PAGE_URL } from "constants/urls";
import { currentUser, questions } from "modules/utilities/utility";
import RootCause from "./RootCause";
import FormulationCanvas from "../../form_formulation/FormulationCanvas";
import { RETAKE_FORM_ACCESS } from "modules/form_formulation/formData";
import {
  useConsultFeedback,
  useMoengageEventData,
  useOfflineCustomerData,
  useRootCauseData
} from "./store";

function SideSubTab({ customer }) {
  const [activeTab, setActiveTab] = useState("");
  const [feedbackData, setCustomConsultFeedbackForm] = useState({});
  const {
    consultFeedbackData,
    fetchFeedbackData,
    prescriptionData,
    fetchPrescriptionData,
    caseFormResponses,
  } = useConsultFeedback();
  const [syntheticId, setSyntheticId] = useState();
  const { rootCauseData, rootCauseLoading, fetchRootCauseData } =
      useRootCauseData();
  const [rootData, setRootData] = useState([]);
  const { eventMoengData, eventMoengLoading, fetchEventMoengData } =
      useMoengageEventData();

  const { offlineCustomerInfo, fetchOfflineCustomerInfo } = useOfflineCustomerData();

  useEffect(() => {
    if(customer){
      fetchOfflineCustomerInfo(customer.id);
    }
  }, [customer]);

  const [newFormulationEventData, setNewFormulationEventData] = useState([]);

  const excludeQuestions = [
    "healthConditionComment",
    "treatmentOtherComment",
    "crmMentionedTimeComments",
    "prevSupplementComment",
  ];

  const normalQuestions = [
    "healthCondition",
    "prevSupplementConsumed",
    "crmMentionedTime",
  ];

  useEffect(() => {
    setRootData(rootCauseData);
  }, [rootCauseData]);

  useEffect(() => {
    setNewFormulationEventData(eventMoengData);
  }, [eventMoengData]);

  const commonPrescriptionQuestion = {
    comments: "Comments",
    comment: "Comment",
    language: "Language",
    kitInfoFlag: "Explained Oral meds.",
    dosageInfoFlag: "Explained about Dosage.",
    topical_os_informed: "Explained Combined usage of topicals(oil/Serum).",
    healthCondition:
        "Do you have any health/medical conditions that we should be aware of?",
    prevSupplementConsumed:
        " Any medicines / supplements being consumed? If yes, which ones.",
    beforeTreatment:
        "What treatments have you done for your hair/scalp before starting the traya plan?",
    breakUpTimelineExpectation:
        "Explained the breakup of the timeline and set clear expectations on customer goals ?",
    crmMentionedTime: " Any changes in CRM mentioned Timeline ?",
    minoxidilInformed:
        "Informed the customer about minoxidil/recap hair shedding ?",
    appInfo:
        "Traya app features explained and guided daily check in to build a habit.",
    herbalFlag: "Inform about oral supplements safety - herbal/ayurvedic.?",
    treatmentStart:
        "Start the treatment immediately. I am marking your start date.",
    queryInfo:
        "Remind the customer - if they have any queries further to please book a call with us. Can be done via app/whatsapp.",
    ongoing_treatment: "Ongoing Treatment",
    medication_allergy: "Medication Allergy",
    minoxidil_info: "Using Minoxidil",
    past_hair_treatment: "Past Hair Treatment",
    order_status: "Order Status",
    callResponse: "Call Responses",
    mood: "Mood",
    customer_adherence: "Customer Adherence",
    adherence_reason: "Adherence Reason",
    any_product_reaction: "Any product reaction",
    gut_health: "Gut Health",
    energy: "Energy",
    hair_loss: "Hair Loss",
    hair_growth: "Hair Growth",
    product_reaction: "Product Reaction",
    doctor_intervention_required: "Doctor Intervention Required",
    not_ordering_reason: "Not ordering Reason",
    medical_condition: "Medical Condition",
    interested_in_community: "Interestedn in Community"
  };

  useEffect(() => {
    if(customer){
      console.log('useEffect ==>', customer);
      if (activeTab === 'prescription') {
        fetchPrescriptionData(customer.id);
      }
      if (activeTab === 'consult') {
        fetchFeedbackData(customer.id, setCustomConsultFeedbackForm);
      }
    }
  }, [customer, activeTab]);

  useEffect(() => {
    setCustomConsultFeedbackForm(consultFeedbackData.feedback);
  }, [consultFeedbackData]);

  useEffect(() => {
    if(customer){
      const sortedData = sortData(customer?.responses);
      let firstEntrySyntheticID = null;
      const keys = Object.keys(sortedData);
      for (const key of keys) {
        if (sortedData[key] === sortedData[activeTab]) {
          firstEntrySyntheticID = sortedData[activeTab][0].synthetic_id;
          setSyntheticId(firstEntrySyntheticID);
          break; // Break after getting the first entry's synthetic ID
        }
      }
    }
  }, [activeTab]);

  useEffect(() => {
    if (syntheticId) {
      fetchRootCauseData(syntheticId);
    }
  }, [syntheticId]);

  useEffect(() => {
    if (customer && customer.id) {
      fetchEventMoengData(customer.id, 'new_formulation');
    }
  }, [customer]);

  function sortData(responses) {
    const data = responses
        .sort((responseA, responseB) => {
          return new Date(responseA.created_at) - new Date(responseB.created_at);
        })
        .reduce((acc, response) => {
          const synId = response.synthetic_id;
          acc[synId] = acc[synId] || [];
          acc[synId].push({
            displayDate: moment(response.updated_at).format(
                "MMM Do YYYY, h:mm a"
            ),
            ...response,
          });
          return acc;
        }, {});

    const sortedData = Object.values(data).reduce((acc, entry) => {
      const date = entry[0].displayDate;
      acc[date] = entry;
      return acc;
    }, {});
    return sortedData;
  }

  function customIsEmpty(value) {
    if (typeof value === 'number' || typeof value === 'boolean') {
      return false;
    }
    return isEmpty(value);
  }

  const redirectToResultPage = (value, gender) => {
    const cohort = value.find((i) => i.question_id === "cohort");
    if (value[0]?.source === "cholesterol") {
      window.open(RESULT_CHOLESTEROL(value[0]?.synthetic_id, cohort?.response));
    } else {
      window.open(
          RESULT_PAGE_URL(value[0]?.synthetic_id, cohort?.response, gender)
      );
    }
  };

  // Generate tab items for the Tabs component
  const generateTabItems = () => {
    const sorted = sortData(caseFormResponses || []);

    // Initialize items array
    const items = [];

    // Always add offline tab, but disable it if isOfflineCustomer is false
    items.push({
      key: "offline",
      label: (
          <span className="px-3 py-2" style={{ minWidth: "5rem", display: "inline-block" }}>
          Offline Info
        </span>
      ),
      children: (
          <div
              style={{ height: "23.8rem" }}
              className="border-top scrollable-container"
          >
            {offlineCustomerInfo?.isOfflineCustomer ? (
                <div className="p-4">
                  <div className="bg-light shadow-sm rounded-3 p-3 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <div className="bg-primary bg-opacity-10 p-2 rounded-circle me-3">
                        <i className="fas fa-user text-primary"></i>
                      </div>
                      <div>
                        <h5 className="mb-0 fw-bold text-primary">{offlineCustomerInfo?.employeeName || "Employee"}</h5>
                      </div>
                    </div>

                    <Button
                        type="primary"
                        icon={<LinkOutlined />}
                        onClick={() => window.open(offlineCustomerInfo?.result, '_blank')}
                    >
                      View Result
                    </Button>
                  </div>
                </div>
            ) : (
                <div className="h-100 d-flex align-items-center justify-content-center">
                  <div className="text-center p-4">
                    <div className="mb-4 text-muted">
                      <NoData style={{ maxHeight: "120px" }} />
                    </div>
                    <h5 className="text-secondary">No offline customer information available</h5>
                    <p className="text-muted mt-2">This customer is not registered as an offline customer.</p>
                  </div>
                </div>
            )}
          </div>
      )
    });

    // Add prescription tab
    items.push({
      key: "prescription",
      label: (
          <span className="px-3 py-2" style={{ minWidth: "5rem", display: "inline-block" }}>
          Prescription Info
        </span>
      ),
      children: (
          <div
              style={{ height: "23.8rem", overflowY: "scroll" }}
              className="border-top scrollable-container"
          >
            {Object.keys(prescriptionData).length ? (
                <div className="grid-container">
                  {Object.keys(prescriptionData).map((question) => {
                    if (
                        excludeQuestions.includes(question) ||
                        customIsEmpty(prescriptionData[question]) ||
                        prescriptionData[question] === "N/A"
                    ) {
                      return null;
                    }
                    return (
                        <div key={question} className="p-2 border rounded-2 my-2 mx-2 shadow">
                          <div className="text-black border-bottom p-2">
                            {commonPrescriptionQuestion[question]}
                          </div>
                          <p className="m-0 text-success p-2">
                            {normalQuestions.includes(question)
                                ? prescriptionData[question] === "Yes"
                                    ? question === "healthCondition"
                                        ? (prescriptionData?.healthConditionComment || "No")
                                        : question === "prevSupplementConsumed"
                                            ? (prescriptionData?.prevSupplementComment || "No")
                                            : question === "crmMentionedTime"
                                                ? (prescriptionData?.crmMentionedTimeComments || "No")
                                                : "No"
                                    : "No"
                                : prescriptionData[question] === true
                                    ? "Yes"
                                    : prescriptionData[question] === false
                                        ? "No"
                                        : prescriptionData[question]}
                          </p>
                        </div>
                    );
                  })}
                </div>
            ) : (
                <div className="h-100 d-flex align-items-center justify-content-center">
                  <div className="text-center p-4">
                    <div className="mb-4 text-muted">
                      <NoData style={{ maxHeight: "120px" }} />
                    </div>
                    <h5 className="text-secondary">No Prescription information available</h5>
                    <p className="text-muted mt-2">There is no prescription assigned to customer.</p>
                  </div>
                </div>
            )}
          </div>
      )
    });

    // Add consult tab
    items.push({
      key: "consult",
      label: (
          <span className="px-3 py-2" style={{ minWidth: "5rem", display: "inline-block" }}>
          Consult
        </span>
      ),
      children: (
          <div
              style={{ height: "23.8rem", overflowY: "scroll" }}
              className="border-top scrollable-container"
          >
            {feedbackData && Object.keys(feedbackData).length ? (
                <div className="grid-container">
                  {questions.map((question) => {
                    return (
                        feedbackData[question] && (
                            <div key={question} className="p-2 border rounded-2 my-2 mx-2 shadow">
                              <div className="text-black border-bottom p-2">
                                {question}
                              </div>
                              <p className="m-0 text-success p-2">
                                {feedbackData[question]}
                              </p>
                            </div>
                        )
                    );
                  })}
                </div>
            ) : (
                <div className="h-100 d-flex align-items-center justify-content-center">
                  <div className="text-center p-4">
                    <div className="mb-4 text-muted">
                      <NoData style={{ maxHeight: "120px" }} />
                    </div>
                    <h5 className="text-secondary">No Consultation information available</h5>
                    <p className="text-muted mt-2">There is no Consultation information for this customer.</p>
                  </div>
                </div>
            )}
          </div>
      )
    });

    // Add date tabs
    Object.entries(sorted).forEach(([date, responses]) => {
      const syntheticId = responses[0]?.synthetic_id;
      const moengageData = newFormulationEventData?.eventDetails?.find(data => {
        return data?.generalAttributes?.syntheticId === syntheticId;
      });

      items.push({
        key: date,
        label: (
            <span className="px-3 py-2" style={{ minWidth: "13rem", display: "inline-block" }}>
            {date}
          </span>
        ),
        children: (
            <div key={date}>
              <div className="d-flex align-items-center justify-content-between px-3 border rounded p-2 mx-3">
                <div className="d-flex align-items-center">
                  <div className="fw-bold text-primary">Root Cause : </div>
                  <div className="ms-1">
                    <RootCause rootData={rootData} />
                  </div>
                </div>
                <div className="text-end">
                  {RETAKE_FORM_ACCESS.includes(currentUser) &&
                      <span><FormulationCanvas responses={responses} customer={customer} /> </span>
                  }
                  {customer.user.gender === "F" && (
                      <span
                          className="mx-2"
                          onClick={() => {
                            redirectToResultPage(responses, "F");
                          }}
                      >
                    <SoftBadge className="p-3 cursor-pointer">
                      Female Result
                    </SoftBadge>
                  </span>
                  )}
                  <span
                      onClick={() => {
                        redirectToResultPage(responses, "M");
                      }}
                  >
                  <SoftBadge className="p-3 cursor-pointer">Result</SoftBadge>
                </span>
                </div>
              </div>
              <div className="d-flex flex-fill gap-2 border rounded p-2 px-3 mx-3 mt-1 align-items-center justify-content-between flex-wrap">
                {[
                  {
                    label: "Eligible for new formulation",
                    value: moengageData?.generalAttributes?.applicable || "N/A",
                  },
                  {
                    label: "Has new formulation",
                    value: moengageData?.generalAttributes?.new_formulation_visible || "N/A",
                  },
                  {
                    label: "New formula product Name",
                    value:
                        Array.isArray(moengageData?.generalAttributes?.product_name)
                            ? moengageData.generalAttributes.product_name.join(" | ")
                            : "N/A"
                  },
                  {
                    label: "New formula tags",
                    value: Array.isArray(moengageData?.generalAttributes?.product_name)
                        ? moengageData.generalAttributes.tags.join(" | ")
                        : "N/A"
                  },
                ].map((item, index) => (
                    <div
                        key={index}
                        className="d-flex flex-column flex-grow-1 text-truncate"
                        style={{ whiteSpace: "nowrap" }}
                    >
                  <span className="fw-bold text-primary">
                    {item.label}: <span className="fw-bold text-black text-capitalize">{`${item.value}`}</span>
                  </span>
                    </div>
                ))}
              </div>
              <div
                  style={{
                    height: "19.8rem",
                    overflowY: "scroll",
                  }}
                  className="mt-1 px-3 scrollable-container grid-container"
              >
                {responses.map((response) => {
                  return (
                      <div
                          key={response.id}
                          className="border rounded my-2 shadow"
                          style={{ padding: "1rem" }}
                      >
                        <p className="m-0 p-2 border-bottom">
                          {response.question}
                        </p>
                        <p className="m-0 p-2 text-success">
                          {response.response}
                        </p>
                      </div>
                  );
                })}
              </div>
            </div>
        )
      });
    });

    return items;
  };

  return (
      <div className="side-sub-ab scrollable-container shadow rounded">
        <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={generateTabItems()}
            className="w-100"
            tabBarStyle={{
              overflowX: "auto",
              whiteSpace: "nowrap",
              padding: "0.5rem",
            }}
            type="card"
            size="small"
        />

        {isEmpty(activeTab) && (
            <div style={{ height: "23.8rem", overflowY: "scroll" }} className="border-top scrollable-container">
              <div className="d-flex align-items-center h-100 fw-bold text-primary gap-3 flex-column">
                <span> Please Select Tab to get data! </span>
                <NoData />
              </div>
            </div>
        )}
      </div>
  );
}

export default SideSubTab;